import React, { useMemo, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import {
  validateAuth0Config,
  type Auth0ConfigValidationResult,
} from '@/config/auth0';
import { cn } from '@/lib/utils';

const DISMISS_KEY = 'auth0-config-banner-dismissed';

const toneClasses = {
  error: {
    container: 'bg-red-50 border-red-200 text-red-900',
    icon: 'text-red-600',
  },
  warning: {
    container: 'bg-amber-50 border-amber-200 text-amber-900',
    icon: 'text-amber-600',
  },
};

interface Auth0ConfigBannerProps {
  validation?: Auth0ConfigValidationResult;
}

const Auth0ConfigBanner: React.FC<Auth0ConfigBannerProps> = ({
  validation: providedValidation,
}) => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.sessionStorage.getItem(DISMISS_KEY) === 'true';
  });

  const validation = useMemo(
    () => providedValidation ?? validateAuth0Config(),
    [providedValidation],
  );
  const hasIssues = validation.issues.length > 0;
  const hasBlockingIssues = validation.issues.some(
    (issue) => issue.level === 'error',
  );

  if (!hasIssues || dismissed) {
    return null;
  }

  const tone = hasBlockingIssues ? toneClasses.error : toneClasses.warning;

  const handleDismiss = () => {
    setDismissed(true);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(DISMISS_KEY, 'true');
    }
  };

  return (
    <div className={cn('border-b px-4 py-3 text-sm', tone.container)}>
      <div className="mx-auto flex max-w-5xl items-start gap-3">
        <AlertTriangle className={cn('mt-0.5 h-5 w-5 shrink-0', tone.icon)} />
        <div className="flex-1 space-y-1">
          <p className="font-semibold">
            Auth0 configuration requires attention.
          </p>
          <ul className="list-disc space-y-0.5 pl-5">
            {validation.issues.map((issue) => (
              <li key={`${issue.envKey ?? issue.message}`}>
                {issue.message}
              </li>
            ))}
          </ul>
          <p className="text-xs opacity-80">
            Confirm backend (AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CLIENT_SECRET, AUTH0_PKCE_STATE_SECRET, AUTH0_CALLBACK_URL) and frontend (`VITE_AUTH0_*`) variables are populated before enabling Auth0 in QA or production. Refer to the Auth0 config runbook for the complete checklist.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-full p-1 transition-colors hover:bg-black/10"
          aria-label="Dismiss Auth0 configuration warning"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Auth0ConfigBanner;
